/*
 * Developed by CQ Inversiones SAS.  Copyright (c) 2024. All rights reserved.
 * Desarrollado por CQ Inversiones SAS.  Copyright (c) 2024. Todos los derechos reservados.
 */

import {Injectable} from '@angular/core';
import {AbstractControl, ValidatorFn, Validators} from '@angular/forms';
import {ZbRegexClass, ZbRegexEnum, alphanumeric} from '@zibanu/lib';
import {NO_INDEX_FIND} from '@shared/constants';

export const REGEX_MOBILE = (min: number = 10, max: number = 10) => `^\\d{${min},${max}}$`;
export type userProfile = 'name' | 'lastname';
export type group = 'name';
export type userField = 'name' | 'lastname';
export type deviceModelVariable = 'sensor' | 'variable' | 'formula'| 'unit'| 'period';
export type deviceModel = 'name' | 'enable' | 'frequency';
export type deviceModelCommand = 'name' | 'req' | 'command';
export type commonFields = 'paragraph' | 'req';
export type customerFields = 'nit' | 'name' | 'address' | 'phone' | 'initial';
export type contractFields = 'numberContract';
export type iconsFields = 'name' | 'description';
export type locationFields = 'name' | 'description' | 'latitude' | 'longitude';
export type sensorFields = 'name' | 'vendor' | 'ref' | 'type' | 'class';
export type sensorSerialFields = 'type' | 'sot' | 'eot' | 'dataLength' | 'separator';
export type sensorVarFields =
  'name'
  | 'short'
  | 'icon'
  | 'measure'
  | 'type'
  | 'calc'
  | 'reads'
  | 'readsDirect'
  | 'min'
  | 'max'
  | 'start'
  | 'length'
  | 'cvFormula'
  | 'rvFormula';
export type commandsFields = 'name' | 'osCommand'
export type contactFields = 'name' | 'dial_code' | 'local_phone' | 'int_phone'
export type parametersFields = 'nameGroup' | 'nameValue' | 'type' | 'value' | 'parent'
export type devices = 'deviceModel' | 'serial'
export type triggers = 'name' | 'selectionRequired'
export type dataBasic = 'selectionRequired'
export type param = 'value' | 'selectionRequired'
export type varLimit = 'value'
export type portVariable = 'name' | 'short' | 'max' | 'min' | 'formulaFS' | 'formulaFM' | 'formulaFMOrFS';
export const MAX_LENGTH = 9999;
export const TEN = 10;
const THOUSAND = 1000;
const ONE_HUNDRED_FIFTY = 150;
const TWO_HUNDRED_FIFTY_FIVE = 255;
const MAX_NUMBERS_COUNT = 18;
const MAX_LIMIT_VALUE = MAX_NUMBERS_COUNT + 1;
export const THIRTY_TWO = 32;
const FIFTY = 50;
const MAX_NAME_LENGTH = 60;
const MIN_NAME_LENGTH = 2;
export const REGEX_NATURAL_NAME: string = '^[a-zA-Z0-9]([a-zA-Z0-9-¡!#$%&()*+.@/_\\s]*[a-zA-Z0-9])?$';
export const REGEX_ADDRESS: string = '^[a-zA-Z0-9]([a-zA-Z0-9-.,_#/:()\\s]*[a-zA-Z0-9])?$';
export const REGEX_LOCATION_NAMES: string = '^[a-zA-Z0-9]([ A-Za-z0-9¡!#$%&()*+\\-.@/_]*[a-zA-Z0-9])?$';
export const REGEX_ICON_DESCRIPTION: string = '^[a-zA-Z0-9-¡!#$%&()*+.@/_]([a-zA-Z0-9-¡!#$%&()*+.@/_\\s]*[a-zA-Z0-9-¡!#$%&()*+.@/_])?$';
export const REGEX_CONTACT_NAME: string = '^[a-zA-Z]([a-zA-Z\\s]*[a-zA-Z])?$';


@Injectable({
  providedIn: 'root'
})
export class MkadValidators {
  private readonly regex = ZbRegexClass;
  private readonly type: typeof ZbRegexEnum = ZbRegexEnum;
  readonly sotEotValidators = (): ValidatorFn[] => {
    return [
      Validators.required,
      Validators.maxLength(ONE_HUNDRED_FIFTY),
      Validators.pattern(new this.regex(this.type.hexList).getExpression),
    ];
  };
  readonly startLengthValidators = (maxLength: number, required: boolean): ValidatorFn[] => {
    return [
      Validators.min(1),
      Validators.max(maxLength),
      Validators.pattern(new this.regex(this.type.numeric).getExpression),
    ].concat(required ? [Validators.required] : []);
  };

  common: Record<commonFields, () => ValidatorFn[]> = {
    paragraph: (): ValidatorFn[] => [Validators.required],
    req: (): ValidatorFn[] => [Validators.required],
  };
  userProfile: Record<userProfile, () => ValidatorFn[]> = {
    name: (): ValidatorFn[] => [
      Validators.required,
      Validators.maxLength(30),
      Validators.pattern(
        new this.regex(this.type.custom, {
          custom: '^(?! )[a-zA-Z]+(?: [a-zA-Z]+)*(?<! )$',
        }).getExpression,
      ),
    ],
    lastname: (): ValidatorFn[] => [
      Validators.required,
      Validators.maxLength(30),
      Validators.pattern(
        new this.regex(this.type.custom, {
          custom: '^(?! )[a-zA-Z]+(?: [a-zA-Z]+)*(?<! )$',
        }).getExpression,
      ),
    ],
  };
  customer: Record<customerFields, () => ValidatorFn[]> = {
    nit: (): ValidatorFn[] => [
      Validators.required,
      Validators.pattern(
        new this.regex(this.type.custom, {
          custom: '^(?!000000$|123456$)(?=[a-zA-Z0-9]{6,20}$)(?!.*?[a-zA-Z]*?0{6})[a-zA-Z0-9]*[^ñ]*$',
        }).getExpression,
      ),
    ],
    name: (): ValidatorFn[] => [
      Validators.required,
      Validators.maxLength(MAX_NAME_LENGTH),
      Validators.minLength(MIN_NAME_LENGTH),
      Validators.pattern(new this.regex(this.type.custom, {custom: REGEX_NATURAL_NAME}).getExpression),
      noMultipleSpaceValidator(),
    ],
    address: (): ValidatorFn[] => {
      const minLength = 6;
      const maxLength = 100;
      return [
        Validators.required,
        Validators.minLength(minLength),
        Validators.maxLength(maxLength),
        Validators.pattern(new this.regex(this.type.custom, {custom: REGEX_ADDRESS}).getExpression),
        noMultipleSpaceValidator(),
      ];
    },
    phone: (minLength: number = 6, maxLength: number = 15): ValidatorFn[] => {
      return [
        Validators.required,
        Validators.minLength(minLength),
        Validators.maxLength(maxLength),
        Validators.pattern(
          new this.regex(this.type.numeric).getExpression,
        ),
      ];
    },
    initial: (): ValidatorFn[] => {
      const minLength = 2;
      const maxLength = 30;
      return [
        Validators.required,
        Validators.minLength(minLength),
        Validators.maxLength(maxLength),
        Validators.pattern(new this.regex(this.type.custom, {custom: REGEX_NATURAL_NAME}).getExpression),
        noMultipleSpaceValidator(),
      ];
    },
  };
  contract: Record<contractFields, () => ValidatorFn[]> = {
    numberContract: (): ValidatorFn[] => [
      Validators.required,
      Validators.maxLength(30),
      Validators.pattern(
        new this.regex(this.type.custom, {
          custom: '^[a-zA-Z0-9][a-zA-Z0-9-_]+(?: [a-zA-Z0-9-_]+)*[a-zA-Z0-9](?<! )',
        }).getExpression,
      ),
      noMultipleSpaceValidator(),
    ],
  };
  contact: Record<contactFields, () => ValidatorFn[]> = {
    name: (): ValidatorFn[] => [
      Validators.required,
      Validators.pattern(REGEX_CONTACT_NAME),
      Validators.maxLength(FIFTY + TEN),
      noMultipleSpaceValidator(),
    ],
    local_phone: (): ValidatorFn[] => [
      Validators.required,
      Validators.pattern(REGEX_MOBILE())
    ],
    int_phone: (): ValidatorFn[] => [
      Validators.required,
      Validators.pattern(REGEX_MOBILE(6, 15))
    ],
    dial_code: (): ValidatorFn[] => this.common.req(),
  };
  user: Record<userField, () => ValidatorFn[]> = {
    name: (): ValidatorFn[] => [
      Validators.required,
      Validators.maxLength(30),
      Validators.pattern(
        new this.regex(this.type.custom, {
          custom: '^(?! )[a-zA-Z]+(?: [a-zA-Z]+)*(?<! )$',
        }).getExpression,
      ),
      noMultipleSpaceValidator(),
    ],
    lastname: (): ValidatorFn[] => [
      Validators.required,
      Validators.maxLength(30),
      Validators.pattern(
        new this.regex(this.type.custom, {
          custom: '^(?! )[a-zA-Z]+(?: [a-zA-Z]+)*(?<! )$',
        }).getExpression,
      ),
      noMultipleSpaceValidator(),
    ],
  };
  group: Record<group, () => ValidatorFn[]> = {
    name: (): ValidatorFn[] => [
      Validators.required,
      Validators.maxLength(30),
      Validators.pattern(new this.regex(this.type.custom, {custom: REGEX_NATURAL_NAME}).getExpression),
      noDoubleSpaceValidator(),
      noWhitespaceValidator()
    ],
  };
  location: Record<locationFields, () => ValidatorFn[]> = {
    name: (): ValidatorFn[] => [
      Validators.required,
      Validators.maxLength(30),
      Validators.pattern(new this.regex(this.type.custom, {custom: REGEX_LOCATION_NAMES}).getExpression),
      noMultipleSpaceValidator(),
    ],
    description: (): ValidatorFn[] => {
      return [
        Validators.maxLength(MAX_NAME_LENGTH),
        Validators.pattern(new this.regex(this.type.custom, {custom: REGEX_LOCATION_NAMES}).getExpression),
        noMultipleSpaceValidator(),
      ];
    },
    latitude: (): ValidatorFn[] => {
      return [
        Validators.required,
        Validators.pattern(
          new this.regex(this.type.latitude).getExpression,
        ),
      ];
    },
    longitude: (): ValidatorFn[] => {
      return [
        Validators.required,
        Validators.pattern(new this.regex(this.type.longitude).getExpression),
      ];
    },
  };
  sensor: Record<sensorFields, () => ValidatorFn[]> = {
    name: (): ValidatorFn[] => [
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(30),
      Validators.pattern(
        new this.regex(this.type.custom, {
          custom: '^[a-zA-Z0-9\\-_/]+(?: [a-zA-Z0-9\\-_/]+)*[a-zA-Z0-9\\-_/]*$',
        }).getExpression,
      ),
    ],
    vendor: (): ValidatorFn[] => [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(30),
      Validators.pattern(
        new this.regex(this.type.custom, {
          custom: '^[a-zA-Z0-9\\-_&/]+(?: [a-zA-Z0-9\\-_&/]+)*[a-zA-Z0-9\\-_&/]*$',
        }).getExpression,
      ),
    ],
    ref: (): ValidatorFn[] => [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(30),
      Validators.pattern(
        new this.regex(this.type.custom, {
          custom: '^[a-zA-Z0-9\\-._/]+(?: [a-zA-Z0-9\\-._/]+)*[a-zA-Z0-9\\-._/]*$',
        }).getExpression,
      ),
    ],
    type: (): ValidatorFn[] => this.common.req(),
    class: (): ValidatorFn[] => this.common.req(),
  };
  sensorSerial: Record<sensorSerialFields, () => ValidatorFn[]> = {
    type: (): ValidatorFn[] => this.common.req(),
    sot: (): ValidatorFn[] => this.sotEotValidators(),
    eot: (): ValidatorFn[] => this.sotEotValidators(),
    dataLength: (): ValidatorFn[] => [
      Validators.required,
      Validators.min(1),
      Validators.max(MAX_LENGTH),
      Validators.pattern(new this.regex(this.type.numeric).getExpression),
    ],
    separator: (): ValidatorFn[] => [
      Validators.pattern(new this.regex(this.type.hex).getExpression),
    ],
  };
  sensorVar: Record<sensorVarFields, () => ValidatorFn[]> = {
    name: (): ValidatorFn[] => [
      Validators.required,
      Validators.maxLength(30),
      Validators.pattern(
        new this.regex(this.type.custom, {
          custom: '^[a-zA-Z0-9\\-_/()]+(?: [a-zA-Z0-9\\-_/()]+)*$'
        }).getExpression,
      ),
      noWhitespaceValidator(),
    ],
    short: (): ValidatorFn[] => [
      Validators.required,
      Validators.maxLength(5),
      Validators.pattern(
        new this.regex(this.type.custom, {
          custom: '^[a-zA-Z0-9\\-_/]+(?: [a-zA-Z0-9\\-_/]+)*$'
        }).getExpression,
      ),
    ],
    icon: (): ValidatorFn[] => this.common.req(),
    measure: (): ValidatorFn[] => [
      Validators.required,
      Validators.maxLength(TEN),
      Validators.minLength(1),
      Validators.pattern(
        new this.regex(this.type.alphanumeric, {
          extra: '-\\|°º_^%/',
        }).getExpression,
      ),
    ],
    type: (): ValidatorFn[] => this.common.req(),
    calc: (): ValidatorFn[] => this.common.req(),
    reads: (): ValidatorFn[] => [
      Validators.required,
      Validators.min(2),
      Validators.max(5),
      Validators.pattern(new this.regex(this.type.numeric).getExpression),
    ],
    readsDirect: (): ValidatorFn[] => [
      Validators.required,
      Validators.min(1),
      Validators.max(1),
      Validators.pattern(new this.regex(this.type.numeric).getExpression),
    ],
    min: (): ValidatorFn[] => [
      Validators.pattern(
        new this.regex(this.type.custom, {
          custom: '^(-?\\d{1,10}\\.\\d{1,8}|-?\\d{1,10})$',
        }).getExpression,
      ),
    ],
    max: (): ValidatorFn[] => [
      Validators.pattern(
        new this.regex(this.type.custom, {
          custom: '^(-?\\d{1,10}\\.\\d{1,8}|-?\\d{1,10})$',
        }).getExpression,
      ),
    ],
    start: (): ValidatorFn[] => this.startLengthValidators(MAX_LENGTH, true),
    length: (): ValidatorFn[] => this.startLengthValidators(THIRTY_TWO, true),
    cvFormula: (): ValidatorFn[] => [
      Validators.required,
      Validators.minLength(4),
      Validators.maxLength(THOUSAND),
      Validators.pattern('^(?=.*\\[(?:CV|RV)\\])(?!.*\\[CV\\].*\\[RV\\])(?!.*\\[RV\\].*\\[CV\\]).+$'),
      noWhitespaceValidator(),
      noBlankSpaceValidator()
    ],
    rvFormula: (): ValidatorFn[] => [
      Validators.required,
      Validators.minLength(4),
      Validators.maxLength(THOUSAND),
      Validators.pattern('^(?=.*\\[(?:RV)\\])(?!.*\\[CV\\].*\\[RV\\])(?!.*\\[RV\\].*\\[CV\\]).+$'),
      noWhitespaceValidator(),
      noBlankSpaceValidator()
    ],
  };
  parameters: Record<parametersFields, () => ValidatorFn[]> = {
    nameGroup: (): ValidatorFn[] => {
      return [
        Validators.required,
        Validators.maxLength(FIFTY),
        Validators.pattern(
          new this.regex(this.type.custom, {
            custom: '^(?! )[A-Z][a-z0-9_]*[a-z0-9_]+(?<! )$',
          }).getExpression,
        ),
      ];
    },
    nameValue: (): ValidatorFn[] => {
      return [
        Validators.required,
        Validators.maxLength(FIFTY),
        Validators.pattern(
          new this.regex(this.type.custom, {
            custom: '^(?! )(?!_)[a-z0-9][a-z0-9_]*$',
          }).getExpression,
        ),
      ];
    },
    type: (): ValidatorFn[] => [
      Validators.required,
    ],
    value: (): ValidatorFn[] => {
      return [
        Validators.required,
        Validators.maxLength(ONE_HUNDRED_FIFTY),
        Validators.pattern(
          new this.regex(this.type.custom, {
            custom: '^(?! )[A-Za-z0-9¡!#$%&()*+\\-.@/_ ]+(?<! )$',
          }).getExpression,
        ),
        noMultipleSpaceValidator(),
      ];
    },
    parent: (): ValidatorFn[] => [
      Validators.required,
    ],
  };
  commands: Record<commandsFields, () => ValidatorFn[]> = {
    name: (): ValidatorFn[] => [
      Validators.required,
      Validators.maxLength(30),
      Validators.pattern(
        new this.regex(this.type.custom, {
          custom: '^(?! )[A-Z0-9\\-_ ]+(?<! )$',
        }).getExpression,
      ),
      noMultipleSpaceValidator(),
    ],
    osCommand: (): ValidatorFn[] => {
      const maxLength = 200;
      return [
        Validators.required,
        Validators.maxLength(maxLength),
        Validators.pattern(
          new this.regex(this.type.alphanumeric,{
            extra: ' ¡!#$%&()*+\\-.@/_"|'
          }).getExpression,
        ),
        noWhitespaceValidator(),
        noDoubleSpaceValidator(),
      ];
    },
  };
  icons: Record<iconsFields, () => ValidatorFn[]> = {
    name: (): ValidatorFn[] => [
      Validators.required,
      Validators.maxLength(FIFTY),
      Validators.pattern(
        new this.regex(this.type.custom,{
          custom: '^[a-z0-9]+(-[a-z0-9]+)*$',
        }).getExpression,
      ),
      noWhitespaceValidator(),
    ],
    description: (): ValidatorFn[] => [
      Validators.required,
      Validators.maxLength(FIFTY),
      noWhitespaceValidator(),
      noDoubleSpaceValidator(),
      Validators.pattern(new this.regex(this.type.custom, {custom: REGEX_ICON_DESCRIPTION}).getExpression),
    ],
  };
  deviceModel: Record<deviceModel, () => ValidatorFn[]> = {
    name: (): ValidatorFn[] => [
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(FIFTY),
      Validators.pattern(
        new this.regex(this.type.custom, {
          custom: '^[A-Z0-9\\-._]+(?: [A-Z0-9\\-._]+)*$'
        }).getExpression,
      ),
    ],
    enable: (): ValidatorFn[] => [Validators.required],
    frequency: (): ValidatorFn[] => [
      Validators.required,
      Validators.min(0),
      Validators.max(TWO_HUNDRED_FIFTY_FIVE),
      Validators.pattern(new this.regex(this.type.numeric).getExpression),
    ],
  };
  devices: Record<devices, () => ValidatorFn[]> = {
    deviceModel: (): ValidatorFn[] => [
      Validators.required,
    ],
    serial: (): ValidatorFn[] => [
      Validators.required,
      Validators.pattern(
        new this.regex(this.type.custom, {
          custom: '^([0-9A-F]{2}:){5}([0-9A-F]{2})$',
        }).getExpression,
      ),
    ],
  };
  deviceModelVar: Record<deviceModelVariable, () => ValidatorFn[]> = {
    formula: (): ValidatorFn[] => [
      Validators.required,
      Validators.minLength(4),
      Validators.maxLength(THOUSAND),
      FSFormulaValidator(),
      noBlankSpaceValidator()
    ],
    sensor: (): ValidatorFn[] => this.common.req(),
    variable: (): ValidatorFn[] => this.common.req(),
    unit: (): ValidatorFn[] => this.common.req(),
    period: (): ValidatorFn[] => [
      Validators.required,
      Validators.pattern(/^\d+$/),
      Validators.max(THOUSAND-1),
      Validators.min(1)
    ],
  };
  triggers: Record<triggers, () => ValidatorFn[]> = {
    name: (): ValidatorFn[] => {
      return [
        Validators.required,
        Validators.maxLength(FIFTY),
        Validators.pattern(
          new this.regex(this.type.custom, {
            custom: '^(?! )[a-zA-Z0-9\\-_/ ]+(?<! )$',
          }).getExpression,
        ),
        noMultipleSpaceValidator()
      ];
    },
    selectionRequired: (): ValidatorFn[] => [
      Validators.required,
    ]

  };
  devModelCommand: Record<deviceModelCommand, () => ValidatorFn[]> = {
    name: (): ValidatorFn[] => {
      return [
        Validators.required,
        Validators.maxLength(FIFTY),
        Validators.pattern(
          new this.regex(this.type.alphanumeric, {startWitch: `${alphanumeric}_`, endWitch: `${alphanumeric}_`, extra: ' _'}).getExpression,
        ),
        noDoubleSpaceValidator(),
      ];
    },
    command: (): ValidatorFn[] => [
      Validators.required,
      Validators.maxLength(ONE_HUNDRED_FIFTY + FIFTY),
      Validators.pattern(
        new this.regex(this.type.alphanumeric,{
          extra: ' ¡!#$%&()*+\\-.@/_"|'
        }).getExpression,
      ),
      noWhitespaceValidator(),
      noDoubleSpaceValidator(),
    ],
    req: (): ValidatorFn[] => this.common.req()
  };
  dataBasic: Record<dataBasic, () => ValidatorFn[]> = {
    selectionRequired: (): ValidatorFn[] => [
      Validators.required,
    ]
  };
  param: Record<param, () => ValidatorFn[]> = {
    selectionRequired: (): ValidatorFn[] => [
      Validators.required,
    ],
    value: (): ValidatorFn[] => [
      Validators.required,
    ]
  };
  portVariable: Record<portVariable, () => ValidatorFn[]>={
    name: (): ValidatorFn[] => [
      Validators.required,
      Validators.maxLength(30),
      Validators.pattern(
        new this.regex(this.type.custom, {
          custom: '^[a-zA-Z0-9\\-_/()]+(?: [a-zA-Z0-9\\-_/()]+)*$'
        }).getExpression,
      ),
      noWhitespaceValidator(),
    ],
    short: (): ValidatorFn[] => [
      Validators.required,
      Validators.maxLength(5),
      Validators.pattern(
        new this.regex(this.type.custom, {
          custom: '^[a-zA-Z0-9\\-_/]+(?: [a-zA-Z0-9\\-_/]+)*$'
        }).getExpression,
      ),
    ],
    min: (): ValidatorFn[] => [
      Validators.pattern(
        new this.regex(this.type.custom, {
          custom: '^(-?\\d{1,10}\\.\\d{1,8}|-?\\d{1,10})$',
        }).getExpression,
      ),
    ],
    max: (): ValidatorFn[] => [
      Validators.pattern(
        new this.regex(this.type.custom, {
          custom: '^(-?\\d{1,10}\\.\\d{1,8}|-?\\d{1,10})$',
        }).getExpression,
      ),
    ],
    formulaFS: (): ValidatorFn[] => [
      Validators.required,
      Validators.minLength(4),
      Validators.maxLength(THOUSAND),
      FSFormulaValidator()
    ],
    formulaFM: (): ValidatorFn[] => [
      Validators.required,
      Validators.minLength(4),
      Validators.maxLength(THOUSAND),
      FMFormulaValidator()
    ],
    formulaFMOrFS: (): ValidatorFn[] => [
      Validators.required,
      Validators.minLength(4),
      Validators.maxLength(THOUSAND),
      FMOrFSFormulaValidator(),
    ],
  };
  varLimit: Record<varLimit, () => ValidatorFn[]>={
    value: () => [
      Validators.required,
      Validators.minLength(1),
      Validators.maxLength(MAX_LIMIT_VALUE),
      Validators.pattern(
        new this.regex(this.type.custom, {
          custom: '^(-?\\d{1,10}\\.\\d{1,8}|-?\\d{1,10})$',
        }).getExpression
      ),
    ]
  }
}

export function FSFormulaValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    const forbidden = /\{FS\}/.test(control.value);
    return forbidden ? null : {'fsNotPresent': {value: control.value}};
  };
}

export function FMFormulaValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    const forbidden = /\{FM\}/.test(control.value);
    return forbidden ? null : {'fmNotPresent': {value: control.value}};
  };
}

export function FMOrFSFormulaValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    const fmMatch = /\{FM\}/.test(control.value);
    const fsMatch = /\{FS\}/.test(control.value);
    return ((fsMatch && !fmMatch) || (!fsMatch && fmMatch)) ? null : {'fmNotPresent': {value: control.value}};
  };
}

export function noWhitespaceValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    if (!control.value) return null;
    const noWhitespace = control.value.trim();
    return noWhitespace.length !== control.value.length ? {whitespace: true} : null;
  };
}

export function minMaxValidator(): ValidatorFn {
  return (control: AbstractControl): Record<string, boolean> | null => {
    const min = control.get('min');
    const max = control.get('max');
    if (min === null || max === null) return null;
    if ((min.value === '' && max.value === '') || (min.value === null && max.value === null)) return null;
    if ((min.value === '' && max.value === null) || (min.value === null && max.value === '')) {
      return null;
    } else if ((min.value !== '' && max.value === null) || (min.value === null && max.value !== '')) {
      return {'minMax': true};
    }
    if (min.value !== null && max.value !== null) {
      const isRangeValid = parseFloat(min.value) < parseFloat(max.value);
      if (!isRangeValid) {
        return {'minMax': true};
      }
    }
    return null;
  };
}

export function noDoubleSpaceValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    if (control.value && control.value.indexOf('  ') !== NO_INDEX_FIND) {
      return {'doubleSpace': true};
    }
    return null;
  }
}

export function noMultipleSpaceValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    if (control.value && control.value.length > 0 && control.value.includes('  ')) return {'multipleSpace': true};
    return null;
  }
}

export function noBlankSpaceValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    if (control.value && control.value.length > 0 && control.value.includes(' ')) return {'blankSpace': true};
    return null;
  }
}
